import React, { useEffect, useState, useRef } from 'react';
import { apiClient } from '../../lib/api-client';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Users, Plus, Save, Trash2, Image as ImageIcon, Edit2, X } from 'lucide-react';
import { normalizeMediaUrl } from '../../utils/media';
import { toast } from 'sonner';

const AVATAR_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjOTk5IiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0iI2YwZjBmMCIgc3Ryb2tlLXdpZHRoPSIzIj48Y2lyY2xlIGN4PSIzMiIgY3k9IjI0IiByPSIxMiIvPjxwYXRoIGQ9Ik0xNiA1MmgzMiIgLz48cGF0aCBkPSJNMjIgNDQgczIgNiAxMCA2IDEwLTYgMi02IiAvPjwvc3ZnPg==';

type Testimonial = {
  id?: string;
  _id?: string;
  name: string;
  role?: string;
  company?: string;
  avatar_url?: string;
  text: string;
};

interface TestimonialItemProps {
  item: Testimonial;
  index: number;
  isEditing: boolean;
  isUploading: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (id: string, data: Testimonial) => void;
  onUpload: (id: string, file: File) => void;
  onDelete: (item: Testimonial) => void;
  effectiveId: (t: Testimonial) => string | undefined;
}

const TestimonialItem = ({
  item,
  index,
  isEditing,
  isUploading,
  onEdit,
  onCancelEdit,
  onSave,
  onUpload,
  onDelete,
  effectiveId
}: TestimonialItemProps) => {
  const [state, setState] = useState<Testimonial>(item);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setState(item);
  }, [item]);

  const id = effectiveId(item);

  return (
    <div className="rounded p-3 flex items-start gap-3 group">
      <div className="text-gray-400 font-mono text-sm select-none pt-2 w-6 text-right shrink-0">
        {index + 1}.
      </div>
      <img
        src={normalizeMediaUrl(state.avatar_url) || state.avatar_url || AVATAR_PLACEHOLDER}
        alt={state.name}
        className="h-12 w-12 object-cover rounded-full border"
      />
      <div className="flex-1 space-y-2">
        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Input
              placeholder="Name"
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
            />
            <Input
              placeholder="Role"
              value={state.role || ''}
              onChange={(e) => setState({ ...state, role: e.target.value })}
            />
            <Input
              placeholder="Company"
              value={state.company || ''}
              onChange={(e) => setState({ ...state, company: e.target.value })}
            />
            <Textarea
              placeholder="Text"
              value={state.text}
              onChange={(e) => setState({ ...state, text: e.target.value })}
            />
          </div>
        ) : (
          <div>
            <div className="font-medium">{state.name}</div>
            <div className="text-sm text-gray-600">
              {[state.role, state.company].filter(Boolean).join(' • ')}
            </div>
            <div className="text-sm mt-1">{state.text}</div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button size="sm" onClick={() => id && onSave(id, state)}>
                <Save className="h-4 w-4 mr-1" /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={onCancelEdit}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit2 className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
          <input
            ref={uploadInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && id) onUpload(id, file);
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={isUploading}
            onClick={() => uploadInputRef.current?.click()}
          >
            <ImageIcon className="h-4 w-4 mr-1" />
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(item)}
            className="text-red-600 hover:text-red-700 border-red-200"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export function TestimonialsManagement() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<Testimonial>({ name: '', text: '', role: '', company: '' });
  const [creatingAvatarFile, setCreatingAvatarFile] = useState<File | null>(null);
  const [creatingAvatarPreview, setCreatingAvatarPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    testimonialId: string | null;
    testimonialName: string;
  }>({
    open: false,
    testimonialId: null,
    testimonialName: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const effectiveId = (t: Testimonial) => t.id || (typeof t._id === 'string' ? t._id : (t._id as any)?.toString?.());

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getTestimonials();
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch (e: any) {
      setError(e?.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleCreate = async () => {
    if (!creating.name || !creating.text) {
      toast.error('Name and text are required');
      return;
    }
    
    try {
      let finalItem = { ...creating };
      
      // First create the testimonial
      const res = await apiClient.createTestimonial(creating);
      // The backend returns the created object directly or inside data
      const created = res?.data || res;
      finalItem = { ...created };
      
      const newId = effectiveId(created);

      // If there is an avatar file, upload it
      if (creatingAvatarFile && newId) {
        try {
          const uploadRes = await apiClient.uploadTestimonialAvatar(newId, creatingAvatarFile);
          const url = uploadRes?.url || uploadRes?.secure_url || uploadRes?.testimonial?.avatar_url;
          if (url) {
            finalItem.avatar_url = url;
            // Update the local item state with the avatar URL
            // We might need to refresh or just update the list locally
          }
        } catch (err) {
          console.error('Failed to upload avatar:', err);
          toast.error('Testimonial created but avatar upload failed');
        }
      }

      setItems([finalItem, ...items]);
      setCreating({ name: '', text: '', role: '', company: '' });
      setCreatingAvatarFile(null);
      setCreatingAvatarPreview(null);
      await fetchItems();
      toast.success('Testimonial created successfully');
    } catch (e: any) {
      const msg = e?.message || 'Failed to create testimonial';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleUpdate = async (id: string, updated: Testimonial) => {
    try {
      const saved = await apiClient.updateTestimonial(id, {
        name: updated.name,
        text: updated.text,
        role: updated.role || '',
        company: updated.company || '',
        avatar_url: updated.avatar_url || ''
      });
      setItems(items.map(i => (effectiveId(i) === id ? saved : i)));
      setEditingId(null);
      toast.success('Testimonial updated');
    } catch (e: any) {
      const msg = e?.message || 'Failed to update testimonial';
      toast.error(msg);
    }
  };

  const openDeleteDialog = (t: Testimonial) => {
    const id = effectiveId(t);
    if (!id) return;
    setDeleteDialog({
      open: true,
      testimonialId: id,
      testimonialName: t.name
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.testimonialId) return;
    setIsDeleting(true);
    try {
      await apiClient.deleteTestimonial(deleteDialog.testimonialId);
      setItems(items.filter(i => effectiveId(i) !== deleteDialog.testimonialId));
      setDeleteDialog(prev => ({ ...prev, open: false }));
      toast.success('Testimonial deleted');
    } catch (e: any) {
      // If it's a 404, we can consider it deleted
      if (e?.status === 404 || e?.message?.includes('Not found')) {
         setItems(items.filter(i => effectiveId(i) !== deleteDialog.testimonialId));
         setDeleteDialog(prev => ({ ...prev, open: false }));
         toast.success('Testimonial deleted (was not found on server)');
      } else {
        setError(e?.message || 'Failed to delete testimonial');
        toast.error(e?.message || 'Failed to delete testimonial');
        setDeleteDialog(prev => ({ ...prev, open: false }));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpload = async (id: string, file: File | null) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const res = await apiClient.uploadTestimonialAvatar(id, file);
      const url = res?.url || res?.secure_url || res?.testimonial?.avatar_url;
      setItems(items.map(i => (effectiveId(i) === id ? { ...i, avatar_url: url } : i)));
      await fetchItems();
      toast.success('Avatar uploaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to upload avatar');
    } finally {
      setUploadingId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Testimonials</h2>
        </div>
      </div>

      <Card className="p-4">
        <h3 className="font-medium mb-3">Add New Testimonial</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input placeholder="Name" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} />
          <Input placeholder="Role (optional)" value={creating.role || ''} onChange={(e) => setCreating({ ...creating, role: e.target.value })} />
          <Input placeholder="Company (optional)" value={creating.company || ''} onChange={(e) => setCreating({ ...creating, company: e.target.value })} />
          <Textarea placeholder="Testimonial text" value={creating.text} onChange={(e) => setCreating({ ...creating, text: e.target.value })} />
          <div className="flex items-center gap-3">
            <img src={normalizeMediaUrl(creatingAvatarPreview || undefined) || creatingAvatarPreview || AVATAR_PLACEHOLDER} alt={creating.name || 'Avatar'} className="h-12 w-12 object-cover rounded-full border" />
            <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={(e) => { const f = e.target.files?.[0] || null; if (f) { setCreatingAvatarFile(f); setCreatingAvatarPreview(URL.createObjectURL(f)); } }} />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              <ImageIcon className="h-4 w-4 mr-1" /> Upload Avatar
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={handleCreate} disabled={loading}>
            <Plus className="h-4 w-4 mr-1" /> Add Testimonial
          </Button>
        </div>
        {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">Existing Testimonials</h3>
          <Button variant="outline" size="sm" onClick={fetchItems}>Refresh</Button>
        </div>
        {loading ? (
          <div className="text-sm text-gray-600">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-gray-600">No testimonials yet.</div>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) => (
              <TestimonialItem
                key={effectiveId(item) || i}
                index={i}
                item={item}
                isEditing={editingId === effectiveId(item)}
                isUploading={uploadingId === effectiveId(item)}
                onEdit={() => setEditingId(effectiveId(item) || null)}
                onCancelEdit={() => setEditingId(null)}
                onSave={handleUpdate}
                onUpload={handleUpload}
                onDelete={openDeleteDialog}
                effectiveId={effectiveId}
              />
            ))}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!isDeleting) {
            setDeleteDialog(prev => ({ ...prev, open }));
          }
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Testimonial"
        description={`Are you sure you want to delete "${deleteDialog.testimonialName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        variant="minimal"
        isLoading={isDeleting}
      />
    </div>
  );
}
